import { useTranslation } from "@sagot/i18n";
import { useState } from "react";
import { useUploadErrorMessage } from "../../../lib/upload-errors";
import { iconSizeProblem, imageProblem, megabytes, readImageSize, type IconSizeProblem, type ImageProblem, type ImageSize } from "./brand-image";
import { useBrandImageUpload } from "./use-widget-studio";
import { WIDGET_LIMITS } from "./widget-model";

const K = "deployKnowledge.widgetStudio.appearance.iconUpload";

export type LauncherIconStatus =
  | { kind: "idle" }
  | { kind: "busy"; stage: "checking" | "uploading"; name: string }
  | { kind: "ready"; name: string }
  | { kind: "removed" }
  | { kind: "error"; message: string; retry: File | null };

const PROBLEM_KEY: Record<ImageProblem | IconSizeProblem, string> = {
  type: "wrongType",
  empty: "empty",
  size: "tooBig",
  unreadable: "unreadable",
  small: "tooSmall",
  large: "tooLarge",
};

/**
 * The launcher-icon pick flow: the file is checked in the browser (type, 2 MB, 64-4096 px, readable)
 * before anything is sent, then goes through the same presigned upload as the chat avatar. The API
 * checks the finished upload again when the draft is saved. `onChange` receives the new public URL
 * (or `null` for the default bubble); the studio keeps it as a draft until Save.
 */
export function useLauncherIconPicker(agentId: string, onChange: (url: string | null) => void) {
  const { t } = useTranslation();
  const uploadMessage = useUploadErrorMessage();
  const [progress, setProgress] = useState(0);
  const upload = useBrandImageUpload(agentId, setProgress);
  const [status, setStatus] = useState<LauncherIconStatus>({ kind: "idle" });

  const reject = (file: File, problem: ImageProblem | IconSizeProblem, size: ImageSize | null = null) => {
    const message = t(`${K}.${PROBLEM_KEY[problem]}`, {
      name: file.name,
      size: megabytes(file.size),
      width: size?.width,
      height: size?.height,
      min: WIDGET_LIMITS.launcherIconMinPx,
      max: WIDGET_LIMITS.launcherIconMaxPx,
    });
    setStatus({ kind: "error", message, retry: null });
  };

  const send = (file: File) => {
    setProgress(0);
    setStatus({ kind: "busy", stage: "uploading", name: file.name });
    upload.mutate(file, {
      onSuccess: ({ url }) => {
        onChange(url);
        setStatus({ kind: "ready", name: file.name });
      },
      onError: (error) => {
        setStatus({ kind: "error", message: uploadMessage(error, t(`${K}.failed`, { name: file.name })), retry: file });
      },
    });
  };

  const pick = async (file: File | undefined) => {
    if (!file) return;
    const problem = imageProblem(file);
    if (problem) {
      reject(file, problem);
      return;
    }
    setStatus({ kind: "busy", stage: "checking", name: file.name });
    const size = await readImageSize(file);
    const sizeProblem = iconSizeProblem(size);
    if (sizeProblem) reject(file, sizeProblem, size);
    else send(file);
  };

  const remove = () => {
    onChange(null);
    setStatus({ kind: "removed" });
  };

  return { status, progress, busy: status.kind === "busy", pick, send, remove };
}
